<template>
  <div class="app-container booking-logs">
    <el-alert title="按每次 Booking 请求查询，包含未生成订单的失败。请求成功不代表订单最终确认；未完成记录也可能是处理中或进程中断。" type="info" :closable="false" show-icon />
    <el-alert v-if="!auditAvailable" title="Booking审计表尚未初始化，当前无可查询记录。" type="warning" :closable="false" />
    <el-alert v-else-if="!diagnosticsAvailable" title="诊断表尚未初始化：可查看历史请求结果，暂无法按资源编码查询或查看快照。" type="warning" :closable="false" />
    <el-form :inline="true" size="small" class="filters" @submit.native.prevent="search">
      <el-form-item v-for="field in fields" :key="field.key" :label="field.label">
        <el-input v-model.trim="query[field.key]" :placeholder="field.label + '（精确匹配）'" clearable @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="动作">
        <el-select v-model="query.action" clearable placeholder="全部">
          <el-option v-for="action in ['New', 'Cancel', 'Modify']" :key="action" :label="action" :value="action" />
        </el-select>
      </el-form-item>
      <el-form-item label="请求结果">
        <el-select v-model="query.result" clearable placeholder="全部">
          <el-option v-for="(label, value) in results" :key="value" :label="label" :value="value" />
        </el-select>
      </el-form-item>
      <el-form-item label="请求时间">
        <el-date-picker v-model="dates" type="datetimerange" value-format="yyyy-MM-dd HH:mm:ss" start-placeholder="开始时间" end-placeholder="结束时间" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="loading" @click="search">查询</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
    <p class="hint">未指定时间或 BookingID / TraceID 时查询最近30天。资源编码筛选仅覆盖已保存诊断的请求。</p>
    <el-table v-loading="loading" :data="items" border row-key="audit_id">
      <el-table-column prop="audit_id" label="请求ID" width="90" />
      <el-table-column prop="created_at" label="请求时间" width="180" />
      <el-table-column prop="booking_id" label="BookingID" min-width="210" show-overflow-tooltip />
      <el-table-column prop="trace_id" label="TraceID" min-width="250" show-overflow-tooltip />
      <el-table-column prop="action" label="动作" width="85" />
      <el-table-column label="请求结果" width="110">
        <template slot-scope="{ row }"><el-tag :type="resultType(row.result)">{{ results[row.result] || '未知' }}</el-tag></template>
      </el-table-column>
      <el-table-column prop="hotel_code" label="酒店编码" width="110" />
      <el-table-column prop="room_type_code" label="房型编码" width="110" />
      <el-table-column prop="rate_plan_code" label="房价计划" width="110" />
      <el-table-column prop="failure_stage" label="失败阶段" min-width="155" show-overflow-tooltip />
      <el-table-column label="原因 / 响应" min-width="230" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.failure_reason || row.response_message || '未记录' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="90" fixed="right">
        <template slot-scope="{ row }"><el-button type="text" @click="showDetail(row)">详情</el-button></template>
      </el-table-column>
    </el-table>
    <el-pagination class="pagination" :current-page="query.page" :page-size="query.limit" :total="total" :page-sizes="[20, 50, 100]"
      layout="total, sizes, prev, pager, next" @current-change="changePage" @size-change="changeSize" />
    <el-dialog title="Booking请求详情" :visible.sync="detailVisible" width="90%" append-to-body>
      <div v-loading="detailLoading">
        <el-alert v-if="detailError" :title="detailError" type="error" :closable="false" />
        <template v-if="detail">
          <p>请求ID：{{ detail.audit_id }}　BookingID：{{ detail.booking_id }}　动作：{{ detail.action }}</p>
          <p>TraceID：{{ detail.trace_id }}</p>
          <p>请求结果：<el-tag :type="resultType(detail.result)">{{ results[detail.result] || '未知' }}</el-tag></p>
          <p>请求时间：{{ detail.created_at }}　处理结束：{{ detail.completed_at || '未记录' }}</p>
          <p>失败阶段：{{ detail.failure_stage || '无' }}</p>
          <p>原因 / 响应：{{ detail.failure_reason || detail.response_message || '未记录' }}</p>
          <p v-if="detail.order">关联订单：{{ detail.order.system_order_no || detail.order.order_id }}
            <router-link :to="{ path: '/dida/order-monitor', query: { booking_id: detail.booking_id } }">查看订单链路</router-link>
          </p>
          <p v-else>未找到关联订单（请求可能在生成订单前失败）。</p>
          <el-alert v-if="detail.diagnostic_status !== 'available'" :title="diagnosticNotice" type="warning" :closable="false" />
          <template v-else>
            <p class="hint">以下为本次请求保存的快照。推送快照表示本地记录的最后成功推送，不能证明道旅应用时间；刷新入队不代表刷新或重推已完成。</p>
            <p class="process-duration">本次业务处理耗时：{{ durationText(detail.diagnostic.duration_ms) }}</p>
            <el-alert v-if="detail.diagnostic.result" :title="detail.diagnostic.result.description || detail.response_message || '未记录说明'"
              :type="stageType(detail.diagnostic.result.status)" :closable="false" show-icon />
            <el-tabs v-model="activeTab">
              <el-tab-pane v-for="section in sections" :key="section.key" :name="section.key" :label="section.label">
                <template v-if="section.key === 'stages'">
                  <el-table :data="processStages" border empty-text="本次请求未记录处理阶段">
                    <el-table-column label="处理阶段" min-width="180">
                      <template slot-scope="{ row }">{{ row.label || row.stage || '未记录' }}</template>
                    </el-table-column>
                    <el-table-column label="结果" width="110">
                      <template slot-scope="{ row }"><el-tag :type="row.status === 'failure' ? 'danger' : stageType(row.status)">{{ stageStatus(row.status) }}</el-tag></template>
                    </el-table-column>
                    <el-table-column prop="at" label="开始时间" min-width="230" />
                    <el-table-column prop="finished_at" label="结束时间" min-width="230" />
                    <el-table-column label="耗时" width="120">
                      <template slot-scope="{ row }">{{ durationText(row.duration_ms) }}</template>
                    </el-table-column>
                    <el-table-column label="说明" min-width="280">
                      <template slot-scope="{ row }">{{ row.message || '历史记录未保存阶段说明' }}</template>
                    </el-table-column>
                  </el-table>
                </template>
                <pre v-else>{{ format(detail.diagnostic[section.key]) }}</pre>
              </el-tab-pane>
            </el-tabs>
          </template>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

const emptyQuery = () => ({ booking_id: '', trace_id: '', hotel_code: '', room_type_code: '', rate_plan_code: '', action: '', result: '', page: 1, limit: 20 })

export default {
  name: 'DidaBookingLogs',
  data() {
    return {
      query: emptyQuery(), dates: [], items: [], total: 0, loading: false,
      diagnosticsAvailable: true, auditAvailable: true, listSequence: 0, detailSequence: 0,
      detailVisible: false, detailLoading: false, detailError: '', detail: null, activeTab: 'stages',
      results: { success: '请求成功', failure: '请求失败', pending: '未完成', unknown: '结果未知' },
      fields: [{ key: 'booking_id', label: 'BookingID' }, { key: 'trace_id', label: 'TraceID' },
        { key: 'hotel_code', label: '酒店编码' }, { key: 'room_type_code', label: '房型编码' }, { key: 'rate_plan_code', label: '房价计划' }],
      sections: [{ key: 'stages', label: '处理阶段' }, { key: 'validation', label: '校验详情' }, { key: 'request', label: '请求业务数据' },
        { key: 'source', label: '源库存 / 价格 / 规则' }, { key: 'published', label: '推送快照' },
        { key: 'recovery', label: '刷新与重推触发' }]
    }
  },
  computed: {
    processStages() {
      const stages = this.detail && this.detail.diagnostic && this.detail.diagnostic.stages
      return Array.isArray(stages) ? stages.filter(stage => stage && typeof stage === 'object' && !Array.isArray(stage)) : []
    },
    diagnosticNotice() {
      if (this.detail && this.detail.diagnostic_status === 'table_missing') return '诊断表尚未初始化，暂无本次请求快照。'
      if (this.detail && this.detail.diagnostic_status === 'invalid') return '本次诊断记录无法解析，请通过 TraceID 查询服务日志。'
      return '本次请求未保存诊断快照（可能为历史请求、处理中或写入失败），请通过 TraceID 查询服务日志。'
    }
  },
  created() {
    const routeQuery = (this.$route && this.$route.query) || {}
    for (const key of ['booking_id', 'trace_id']) {
      if (typeof routeQuery[key] === 'string') this.query[key] = routeQuery[key]
    }
    this.getList()
  },
  beforeDestroy() { this.listSequence++; this.detailSequence++ },
  methods: {
    durationText(value) { return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value + ' ms' : '未记录' },
    stageStatus(status) { return ({ success: '已完成', failure: '未通过', pending: '待供应商确认', running: '处理中' })[status] || '未记录' },
    stageType(status) { return ({ success: 'success', failure: 'error', pending: 'warning', running: 'info' })[status] || 'info' },
    resultType(result) { return ({ success: 'success', failure: 'danger', pending: 'warning', unknown: 'info' })[result] || 'info' },
    format(value) { return value == null ? '本次请求未记录此项' : JSON.stringify(value, null, 2) },
    search() { this.query.page = 1; return this.getList() },
    reset() { this.query = emptyQuery(); this.dates = []; return this.getList() },
    changePage(page) { this.query.page = page; return this.getList() },
    changeSize(size) { this.query.limit = size; return this.search() },
    async getList() {
      const sequence = ++this.listSequence
      this.loading = true
      const params = { ...this.query }
      if (this.dates && this.dates.length === 2) { params.start_at = this.dates[0]; params.end_at = this.dates[1] }
      try {
        const response = await request({ url: '/api/backend/dida/booking-logs', method: 'get', params })
        if (sequence !== this.listSequence) return
        const data = response.data
        this.items = data.items || []; this.total = data.total || 0
        this.diagnosticsAvailable = data.diagnostics_available !== false
        this.auditAvailable = data.audit_available !== false
      } catch (error) {
        if (sequence === this.listSequence) { this.items = []; this.total = 0 }
      } finally {
        if (sequence === this.listSequence) this.loading = false
      }
    },
    async showDetail(row) {
      const sequence = ++this.detailSequence
      this.detail = null; this.detailError = ''; this.detailVisible = true; this.detailLoading = true; this.activeTab = 'stages'
      try {
        const response = await request({ url: '/api/backend/dida/booking-logs/detail', method: 'get', params: { audit_id: row.audit_id } })
        if (sequence === this.detailSequence) this.detail = response.data
      } catch (error) {
        if (sequence === this.detailSequence) this.detailError = '详情加载失败，请关闭后重试。'
      } finally {
        if (sequence === this.detailSequence) this.detailLoading = false
      }
    }
  }
}
</script>

<style scoped>
.booking-logs .filters { margin-top: 18px; }
.booking-logs .pagination { margin-top: 16px; }
.hint { color: #606266; font-size: 13px; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 55vh; overflow: auto; padding: 16px; background: #f5f7fa; }
</style>
